import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";
import Sidebar from "../../components/sidebar/Sidebar";
import LoadingSpinner from "../../components/LoadingSpinner";
import "../accounts/view/ViewAccountPage.css";
import { MUI_TABLE } from '../../utils/muiTableConfig';
import { Username, useMask } from '../../utils/privacyMask';

const fmtDate = ms => ms ? new Date(Number(ms)).toLocaleDateString('en-GB') : '—';

export default function PointsPage() {
  const navigate = useNavigate();
  const { mask } = useMask();

  const [records, setRecords]   = useState([]);
  const [accountMap, setAccountMap] = useState({});  // _id → username
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const API = process.env.REACT_APP_API_URL;

  useEffect(() => {
    (async () => {
      try {
        const [ptsRes, accRes] = await Promise.all([
          axios.get(`${API}/referralpoints`, { headers }),
          axios.get(`${API}/account`, { headers }),
        ]);
        setRecords(Array.isArray(ptsRes.data) ? ptsRes.data : []);
        const map = {};
        accRes.data.forEach(a => { map[a._id] = a.username; });
        setAccountMap(map);
      } catch (err) {
        const status = err.response?.status;
        if (status === 404) {
          setError('Points list endpoint returned 404 — the API server may need restarting.');
        } else {
          setError(err.response?.data?.error || 'Failed to fetch points records');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDelete = async (row) => {
    const label = accountMap[row.accountID] || row.discordUsername || row.discordId || 'this record';
    if (!window.confirm(`Delete all points for ${label}? This cannot be undone.`)) return;
    try {
      // Use _id-based delete (works for all records regardless of whether they have a discordId)
      await axios.delete(`${API}/referralpoints/id/${row._id}`, { headers });
      setRecords(prev => prev.filter(r => String(r._id) !== String(row._id)));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete record');
    }
  };

  const displayName = (row) =>
    (row.accountID && accountMap[row.accountID]) || row.discordUsername || '—';

  const columns = useMemo(() => [
    {
      header: 'Account',
      id: 'account',
      accessorFn: row => displayName(row),
      Cell: ({ row }) => {
        const name = displayName(row.original);
        const accountId = row.original.accountID;
        return accountId ? (
          <button
            onClick={() => navigate(`/account/view/${accountId}`)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, color: '#4F46E5', fontSize: '0.875rem' }}
          >
            <Username value={name} />
          </button>
        ) : (
          <span style={{ fontWeight: 600, color: 'var(--color-text)' }}><Username value={name} /></span>
        );
      },
    },
    {
      header: 'Discord',
      accessorKey: 'discordUsername',
      Cell: ({ cell }) => <span style={{ color: 'var(--color-muted)', fontSize: '0.82rem' }}>{cell.getValue() ? <Username value={cell.getValue()} /> : '—'}</span>,
    },
    {
      header: 'Total Earned',
      accessorKey: 'totalPointsEarned',
      Cell: ({ cell }) => (
        <span style={{ color: '#4F46E5', fontWeight: 700 }}>
          <i className="bi bi-star-fill" style={{ fontSize: '0.7rem', marginRight: 5 }} />
          {mask(cell.getValue()?.toLocaleString() ?? 0, 'number')}
        </span>
      ),
    },
    {
      header: 'Available',
      accessorKey: 'availablePoints',
      Cell: ({ cell }) => (
        <span style={{ color: '#10B981', fontWeight: 600 }}>
          {mask(cell.getValue()?.toLocaleString() ?? 0, 'number')}
        </span>
      ),
    },
    {
      header: 'Spent',
      id: 'spent',
      accessorFn: row => (row.totalPointsEarned ?? 0) - (row.availablePoints ?? 0),
      Cell: ({ cell }) => (
        <span style={{ color: '#EF4444' }}>{mask(cell.getValue()?.toLocaleString(), 'number')}</span>
      ),
    },
    {
      header: 'Last Updated',
      accessorKey: 'updatedAt',
      Cell: ({ cell }) => <span style={{ color: 'var(--color-muted)' }}>{fmtDate(cell.getValue())}</span>,
    },
  ], [accountMap, mask]);

  const table = useMaterialReactTable({
    columns,
    data: records ?? [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['mrt-row-actions'] },
    },
    renderRowActionMenuItems: ({ row }) => [
      row.original.accountID && (
        <MenuItem key="account" onClick={() => navigate(`/account/view/${row.original.accountID}`)} sx={{ m: 0, gap: 1 }}>
          <i className="bi bi-person-fill" style={{ fontSize: '0.9rem', color: 'var(--color-muted)' }} />View Account
        </MenuItem>
      ),
      row.original.discordId && (
        <MenuItem key="view" onClick={() => navigate(`/points/view/${row.original.discordId}`)} sx={{ m: 0, gap: 1 }}>
          <i className="bi bi-eye" style={{ fontSize: '0.9rem', color: 'var(--color-muted)' }} />View Claims
        </MenuItem>
      ),
      <MenuItem key="add" onClick={() => navigate(
        row.original.accountID
          ? `/points/add?accountId=${row.original.accountID}`
          : `/points/add`
      )} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-plus-circle" style={{ fontSize: '0.9rem', color: 'var(--color-muted)' }} />Adjust Points
      </MenuItem>,
      <MenuItem key="delete" onClick={() => handleDelete(row.original)} sx={{ m: 0, gap: 1, color: '#EF4444' }}>
        <i className="bi bi-trash" style={{ fontSize: '0.9rem', color: '#EF4444' }} />Delete
      </MenuItem>,
    ].filter(Boolean),
    ...MUI_TABLE,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Points" />

      <div className="view-page">
        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, color: '#EF4444', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-star-fill" style={{ color: '#4F46E5' }} />
              Points Records
            </h6>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="view-panel-count">{records.length}</span>
              <Link to="/points/add" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                borderRadius: 8, border: '1px solid rgba(79,70,229,0.3)', background: 'rgba(79,70,229,0.08)',
                color: '#4F46E5', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
              }}>
                <i className="bi bi-plus-lg" /> Add Points
              </Link>
            </div>
          </div>
          <MaterialReactTable table={table} />
        </div>
      </div>
    </>
  );
}
